import React from "react";
import {
  DataGrid,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridColDef,
  GridRowParams,
  GridValidRowModel,
  DataGridProps,
} from "@mui/x-data-grid";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import { MuiThemeWrapper } from "../MuiThemeWrapper/MuiThemeWrapper";
import { DataGridToolbar } from "../MuiDataGridToolbar";

interface ServerDataGridProps<
  T extends GridValidRowModel,
> extends DataGridProps {
  rows: T[];
  rowCount: number;
  loading: boolean;
  columns: GridColDef<T>[];
  paginationModel: GridPaginationModel;
  onPaginationChange?: (paginationModel: GridPaginationModel) => void;
  filterModel: GridFilterModel;
  onFilterModelChange: (model: GridFilterModel) => void;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  onRowClick?: (params: GridRowParams<T>) => void;
  filterDebounceMs?: number;
}

const CustomToolbar = React.memo(
  ({ filterModel }: { filterModel: GridFilterModel }) => {
    return <DataGridToolbar filterModel={filterModel} />;
  },
);

export const MuiServerDataGrid = <T extends GridValidRowModel>({
  rows,
  rowCount,
  loading,
  columns,
  paginationModel,
  onPaginationChange,
  filterModel,
  onFilterModelChange,
  sortModel,
  onSortModelChange,
  onRowClick,
  filterDebounceMs = 500,
  ...rest
}: ServerDataGridProps<T>) => {
  const nonHideableColumns = React.useMemo(
    () =>
      columns.map(col => ({
        ...col,
        hideable: false,
      })),
    [columns],
  );

  const ToolbarWithFilterModel = React.useCallback(
    () => <CustomToolbar filterModel={filterModel} />,
    [],
  );

  return (
    <MuiThemeWrapper>
      <DataGrid
        rowHeight={100}
        loading={loading}
        paginationMode="server"
        onPaginationModelChange={onPaginationChange}
        paginationModel={paginationModel}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        sortingOrder={["asc", "desc"]}
        showToolbar
        rowCount={rowCount}
        rows={rows}
        onRowClick={onRowClick}
        columns={nonHideableColumns}
        disableColumnSelector
        slots={{
          toolbar: ToolbarWithFilterModel,
          columnSortedAscendingIcon: () => <FiChevronUp />,
          columnSortedDescendingIcon: () => <FiChevronDown />,
        }}
        filterDebounceMs={filterDebounceMs}
        sx={{
          height: "100%",
          "& .MuiDataGrid-filterPanel": {
            position: "fixed !important",
            transform: "none !important",
            transition: "none !important",
          },
          "& .MuiDataGrid-virtualScroller": {
            overflow: "auto !important",
            scrollBehavior: "smooth",
          },
        }}
        {...rest}
      />
    </MuiThemeWrapper>
  );
};
